import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import CloseIcon from '@material-ui/icons/Close'
import ExportDownloadSignalR from '../../../Components/SignalrSubscribes/ExportDownloadSignalR'
import ExportToCsvModal from '../../../Components/modals/export-to-csv-modal/export-to-csv-modal.component'
import { useAuth } from '../../../Contexts/AuthProvider'
import FleetChart from './FleetChart'
import {
  FLEET_CHART_DATE_TYPES,
  getDateTypeMs,
  colorForUnitIndex,
} from './fleetChartHelpers'
import AlertToastr from 'utils/alert'
import useDeleteDataExport from '../../../Hooks/useDeleteDataExport'
import useDataExport from '../../../Hooks/useDataExport'
import Api from 'Services/Api.service'
import { apiBaseUrl } from 'config'
import { downloadAs } from '../../../helpers'

const FleetChartPanel = ({
  fleetType,
  licenseIds,
  standardChannelName,
  dateType,
  dateOriginMs,
  channelOptions = [],
  seriesByUnit = {},
  channelUnit = '',
  fieldName = '',
  onChannelChange,
  onDateTypeChange,
  onClose,
  translation,
}) => {
  const { token, currentCompany } = useAuth()
  const deleteExportMutation = useDeleteDataExport('FleetView')
  const exportRequest = useDataExport('FleetView')

  const seriesList = useMemo(() => {
    const entries = Object.values(seriesByUnit || {})
    return entries
      .slice()
      .sort((a, b) => String(a.unitName || a.unitUId).localeCompare(String(b.unitName || b.unitUId)))
      .map((entry, index) => ({
        unitUId: entry.unitUId,
        unitName: entry.unitName || entry.unitUId,
        channelName: entry.channelName,
        channelUnit: entry.channelUnit,
        fieldName: entry.fieldName,
        color: entry.color || colorForUnitIndex(index),
        points: entry.points || [],
      }))
  }, [seriesByUnit])

  const resolvedUnit = channelUnit
    || seriesList.find((s) => s.channelUnit)?.channelUnit
    || ''

  const resolvedTitle = fieldName
    || standardChannelName
    || seriesList.find((s) => s.fieldName)?.fieldName
    || ''

  const channelSelectOptions = useMemo(() => {
    if (channelOptions.length) return channelOptions
    if (standardChannelName) {
      return [{ value: standardChannelName, label: standardChannelName }]
    }
    return []
  }, [channelOptions, standardChannelName])

  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportInProgress, setExportInProgress] = useState(false)
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState(() => new Set())
  const hiddenSeriesIdsRef = useRef(hiddenSeriesIds)

  const seriesKey = useMemo(
    () => seriesList.map((s) => s.unitUId).join('|'),
    [seriesList],
  )

  useEffect(() => {
    hiddenSeriesIdsRef.current = hiddenSeriesIds
  }, [hiddenSeriesIds])

  useEffect(() => {
    const next = new Set()
    hiddenSeriesIdsRef.current = next
    setHiddenSeriesIds(next)
  }, [seriesKey, dateOriginMs, standardChannelName, dateType])

  const handleToggleSeriesVisibility = useCallback((unitUId) => {
    if (!unitUId) return
    setHiddenSeriesIds((prev) => {
      const next = new Set(prev)
      if (next.has(unitUId)) next.delete(unitUId)
      else next.add(unitUId)
      hiddenSeriesIdsRef.current = next
      return next
    })
  }, [])

  const endChartDateMs = useMemo(() => {
    if (dateOriginMs == null) return null
    return dateOriginMs + getDateTypeMs(dateType)
  }, [dateOriginMs, dateType])

  const resolvedJobName = useMemo(() => (
    resolvedTitle || standardChannelName || translation?.fleetChartTitleLabel || 'Fleet chart'
  ), [resolvedTitle, standardChannelName, translation?.fleetChartTitleLabel])

  const handleCsvExportCompleted = useCallback(async () => {
    setExportInProgress(false)

    try {
      await exportRequest.refetch()
    } catch (_) {
      /* ignore refetch errors */
    }
  }, [exportRequest])

  const handleExportData = useCallback(async (data) => {
    if (!fleetType || !Array.isArray(licenseIds) || !licenseIds.length) {
      AlertToastr.showErrorAlert('Fleet export: missing fleetType/licenseIds')
      return
    }

    setExportInProgress(true)
    setExportModalOpen(false)

    const hiddenIds = hiddenSeriesIdsRef.current
    const visibleUnitUIds = seriesList
      .map((s) => s.unitUId)
      .filter((uid) => uid && !hiddenIds.has(uid))

    if (!visibleUnitUIds.length) {
      AlertToastr.showErrorAlert('Select at least one unit to export')
      setExportInProgress(false)
      return
    }

    const payload = {
      // same names as modal output + backend contract
      name: data.name,
      delimiter: data.delimiter,
      decimalDelimiter: data.decimalDelimiter,
      timeShift: data.timeShift,
      cultureInfoName: data.cultureInfoName,
      dateTimeFormat: data.dateTimeFormat,
      fleetType,
      licenseIds,
      standardChannelName,
      dateFrom: data.dateFrom,
      dateTo: data.dateTo,
      unitUIds: visibleUnitUIds,
    }

    try {
      await Api.post(`${apiBaseUrl}/fleet/chart-export`, payload)
      await exportRequest.refetch()
    } catch (e) {
      setExportInProgress(false)
      const message = e?.response?.data?.Message || e?.message || 'Fleet chart export failed'
      AlertToastr.showErrorAlert(message)
    }
  }, [fleetType, licenseIds, standardChannelName, exportRequest, seriesList])

  const onDownloadCsv = useCallback(async () => {
    try {
      const url = exportRequest.data?.url
      if (!url) return

      const fileName = exportRequest.data?.fileName || 'fleet-export.csv'
      await downloadAs(url, fileName)
      await deleteExportMutation.mutateAsync()
      await exportRequest.refetch()
    } catch (e) {
      const message = e?.response?.data?.Message || e?.message || 'Fleet chart CSV download failed'
      AlertToastr.showErrorAlert(message)
    }
  }, [deleteExportMutation, exportRequest])

  const exportIsCompleted = exportRequest.data?.isCompleted
  const exportFileName = exportRequest.data?.fileName
  const isExportBuilding = exportInProgress || exportIsCompleted === false
  const isExportDownloadReady = exportIsCompleted === true

  const handleExportButtonClick = useCallback(() => {
    if (isExportDownloadReady) {
      onDownloadCsv()
      return
    }
    if (!isExportBuilding) {
      setExportModalOpen(true)
    }
  }, [isExportBuilding, isExportDownloadReady, onDownloadCsv])

  const exportButtonLabel = isExportDownloadReady
    ? (translation?.downloadBtnLabel || 'Download')
    : isExportBuilding
      ? (translation?.buildingCsvLabel || 'Building CSV…')
      : (translation?.dashboardExportToCsvLabel || 'Export To CSV')

  const exportButtonClassName = [
    'fleetChartExportBtn',
    isExportDownloadReady ? 'isReady' : '',
    isExportBuilding ? 'isBuilding' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="pageBlock">
      <div className="pageCard fleetChartPanel">
        <div className="fleetChartToolbar">
          <div className="fleetChartToolbarTitle">
            <span className="title">
              {translation?.fleetChartTitleLabel || 'Fleet chart'}
            </span>
            {resolvedTitle ? (
              <span className="meta">
                {resolvedTitle}
                {resolvedUnit ? ` (${resolvedUnit})` : ''}
              </span>
            ) : null}
          </div>

          <div className="fleetChartToolbarControls">
            <button
              type="button"
              onClick={handleExportButtonClick}
              disabled={isExportBuilding}
              className={exportButtonClassName}
              title={
                isExportDownloadReady
                  ? (exportFileName || 'CSV')
                  : undefined
              }
            >
              {exportButtonLabel}
            </button>

            <label className="fleetChartControl">
              <span className="fleetChartControlLabel">
                {translation?.fleetChartChannelLabel || 'Channel'}
              </span>
              <Select
                value={standardChannelName || ''}
                onChange={(e) => onChannelChange?.(e.target.value)}
                className="fleetChartSelect"
                disableUnderline
                displayEmpty
              >
                {channelSelectOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </label>

            <label className="fleetChartControl">
              <span className="fleetChartControlLabel">
                {translation?.fleetChartRangeLabel || 'Range'}
              </span>
              <Select
                value={dateType || ''}
                onChange={(e) => onDateTypeChange?.(e.target.value)}
                className="fleetChartSelect"
                disableUnderline
              >
                {FLEET_CHART_DATE_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </label>
          </div>

          <Button
            className="fleetChartCloseBtn"
            onClick={onClose}
            startIcon={<CloseIcon />}
            size="small"
          >
            {translation?.closeButtonLabel || 'Close'}
          </Button>
        </div>

        {exportInProgress ? (
          <ExportDownloadSignalR
            tabType={3}
            onFinish={handleCsvExportCompleted}
            token={token}
            companyId={currentCompany?.id}
          />
        ) : null}

        <ExportToCsvModal
          isOpen={exportModalOpen}
          setOpen={setExportModalOpen}
          jobName={resolvedJobName}
          onExportData={handleExportData}
          onCancel={() => setExportModalOpen(false)}
          currentJob={null}
          onBack={() => setExportModalOpen(false)}
          endChartDate={endChartDateMs}
          startChartDate={dateOriginMs}
          modalStep="chartOptionsCsv"
          jobsBoxesFilters={{}}
        />

        {dateOriginMs != null ? (
          <FleetChart
            key={`${standardChannelName}:${dateType}:${dateOriginMs}`}
            seriesList={seriesList}
            dateOriginMs={dateOriginMs}
            title={resolvedTitle}
            channelUnit={resolvedUnit}
            hiddenSeriesIds={hiddenSeriesIds}
            onToggleSeriesVisibility={handleToggleSeriesVisibility}
          />
        ) : null}
      </div>
    </div>
  )
}

export default FleetChartPanel
